import { ref, computed, watch, type Ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../eventBus';
import { useSOCheckout } from './useSOCheckout';
import { useSOStatusMap } from './useSOStatusMap';
import { phoneToString } from './usePhoneFormat';
import { log, error as logError } from '../utils/DebugLogger';

/**
 * useCheckout — composable for PDV checkout state and business logic.
 *
 * Designed to be consumed by both PDVCheckoutForm.vue (page mode) and
 * PDVCheckoutModal.vue (modal mode). The orderId ref drives data loading;
 * changing it triggers a reload.
 *
 * ---- Composable surface ----
 *
 * Reactive state:
 *   order           — full order object (OrderData | null)
 *   items           — flat item list (OrderItem[])
 *   loading         — true while any async operation is in flight
 *   error           — error message string, null when clean
 *
 * Discount state:
 *   isPercentageDiscount — true = %, false = R$
 *   discountValue        — current input value
 *   isApplyingDiscount   — true while the discount API call is in flight
 *
 * Computed:
 *   groupedItems         — parent items with nested .children arrays
 *   discountAmount       — subtotal - total (in centavos)
 *   valorRestante        — total - received (in centavos)
 *   formattedClientPhone — formatted phone display string
 *
 * Methods (async, all throw on failure):
 *   loadOrder()          — fetch order + items from API
 *   removeItem(itemId)   — delete item, re-fetches on success
 *   confirmOrder()       — finalize the order (draft → confirmed)
 *   cancelOrder()        — cancel the order
 *   uncancelOrder()      — reverse a cancellation (cancelled → draft)
 *   applyDiscount()      — send discount to API, re-fetches on success
 *   handleDiscountTypeChange(isPercentage) — switch R$/%, resets value
 *
 * Pure helpers (no side effects):
 *   formatAttributeDisplayValue(attr) — renders attribute as string
 *   getItemSubtotal(item)             — parent + children total (centavos)
 *   getItemEstimatedDays(item)        — parent + children deadline days
 *   statusLabel(status)               — human-readable order status
 *   financialStatusLabel(status)      — human-readable financial status
 *
 * ---- Shell template structure (mapped to PDVCheckoutForm.vue) ----
 *
 * 1. Header: back button, client info (name + phone), order info (OS# + status)
 * 2. Main layout (2-col grid):
 *    Left — Items
 *      Section header (title + "Serviço" / "Produtos" add buttons)
 *      Items list (item cards with badges, totals, action buttons)
 *      Totals section (discount + final total)
 *    Right — Payment
 *      Financial status (status, received, remaining)
 *      Discount section (type toggle + input + apply button)
 *      Action buttons (Cancelar OS, Gerar OS)
 * 3. Loading state (spinner)
 *
 * ---- Page-vs-modal divergence ----
 * - Page mode: reads orderId from route param; back/laudo actions use router.push
 * - Modal mode: receives orderId via props/event; back/laudo actions use eventBus;
 *   emits 'done' when order finalized so modal auto-closes;
 *   uses v-if="isVisible" to prevent state leak across opens
 */
export function useCheckout(orderIdRef: Ref<number | string | undefined>) {
  const { t, n } = useI18n();
  const { getOrder, fetchItems, removeItem: apiRemoveItem, updateItem: apiUpdateItem, confirmOrder: apiConfirmOrder, cancelOrder: apiCancelOrder, uncancelOrder: apiUncancelOrder, updateDiscount: apiUpdateDiscount } = useSOCheckout();
  const { getOrderStatus, getItemStatus } = useSOStatusMap();

  // ---------------------------------------------------------------------------
  // Types
  // ---------------------------------------------------------------------------
  type Id = number | string;

  interface OrderData {
    so_order_id?: Id;
    so_order_status?: string;
    so_order_financial_status?: string;
    so_order_client_id?: Id;
    so_order_client_name?: string;
    so_order_client_ddi?: string;
    so_order_client_ddd?: string;
    so_order_client_phone?: string;
    so_order_franchise_id?: Id;
    so_order_total?: number;
    so_order_total_recebido?: number;
    so_order_subtotal?: number;
    so_order_discount_value?: number;
    [key: string]: unknown;
  }

  interface OrderItemAttribute {
    so_oiav_id?: Id;
    so_oiav_value?: string;
    so_item_attribute_type?: string;
    so_item_attribute_name?: string;
    [key: string]: unknown;
  }

  interface OrderItem {
    so_order_item_id?: Id;
    so_order_item_parent_item_id?: Id | null;
    so_order_item_type?: string;
    so_order_item_total?: number | string;
    so_order_item_unit_price?: number | string;
    so_order_item_qrcode?: string;
    so_order_item_description?: string;
    so_order_item_deadline_days?: number | string;
    so_order_item_delivered_at?: string;
    so_service_name?: string;
    so_priority_name?: string;
    item_display_status?: string;
    step_total?: number;
    step_completed?: number;
    attributes?: OrderItemAttribute[];
    children?: OrderItem[];
    [key: string]: unknown;
  }

  // ---------------------------------------------------------------------------
  // Reactive state
  // ---------------------------------------------------------------------------
  const order = ref<OrderData | null>(null);
  const items = ref<OrderItem[]>([]);
  const loading = ref(false);
  const error = ref<string | null>(null);

  // Discount state
  const isPercentageDiscount = ref(false); // false = R$, true = %
  const discountValue = ref(0);
  const isApplyingDiscount = ref(false);

  // ---------------------------------------------------------------------------
  // Computed
  // ---------------------------------------------------------------------------
  const groupedItems = computed<OrderItem[]>(() => {
    const parentItems = items.value.filter((i: OrderItem) => !i.so_order_item_parent_item_id);
    return parentItems.map((parent: OrderItem) => ({
      ...parent,
      children: items.value.filter((i: OrderItem) => i.so_order_item_parent_item_id === parent.so_order_item_id),
    }));
  });

  const discountAmount = computed<number>(() => {
    if (!order.value) return 0;
    return (order.value.so_order_subtotal || 0) - (order.value.so_order_total || 0);
  });

  const valorRestante = computed<number>(() => {
    if (!order.value) return 0;
    return Math.max(0, (order.value.so_order_total || 0) - (order.value.so_order_total_recebido || 0));
  });

  // Total already received (centavos). Single source of truth for the FE advisory
  // received-floor check; the authoritative guard lives in the backend invariant.
  const totalRecebido = computed<number>(() => order.value?.so_order_total_recebido || 0);

  // An OS that has received any payment carries the received-floor risk on edits.
  const isPaid = computed<boolean>(() => totalRecebido.value > 0);

  // F1 editability policy: an OS is editable in EVERY status except 'cancelled'
  // (including 'entregue'). The only hard stop on lowering edits is the
  // received-floor invariant, enforced by the backend and surfaced as feedback.
  const canAddItems = computed<boolean>(() => order.value?.so_order_status !== 'cancelled');

  // R1: an order revived by uncancel goes back to 'draft' with its work orders
  // already alive — confirmOrder() is not idempotent, so reconfirming would
  // duplicate work orders and re-decrement stock. Each service item already
  // carries step_total from its work order's step progress (0 when no work
  // order exists yet), so this reuses data already on the screen instead of
  // a new endpoint/field.
  const hasExistingWorkOrders = computed<boolean>(() =>
    items.value.some((i: OrderItem) => (i.step_total || 0) > 0),
  );

  const formattedClientPhone = computed<string>(() => {
    if (!order.value || !order.value.so_order_client_phone) return '';
    return phoneToString({
      countryCode: order.value.so_order_client_ddi || '+55',
      areaCode: order.value.so_order_client_ddd || '',
      phoneNumber: order.value.so_order_client_phone || '',
    });
  });

  // ---------------------------------------------------------------------------
  // Helpers (pure)
  // ---------------------------------------------------------------------------
  function formatAttributeDisplayValue(attr?: OrderItemAttribute): string {
    if (!attr) return '-';
    if (attr.so_item_attribute_type === 'boolean') {
      return attr.so_oiav_value === '1' ? t('Sim') : t('Não');
    }
    return attr.so_oiav_value || '-';
  }

  function getItemSubtotal(item: OrderItem): number {
    let total = parseInt(String(item.so_order_item_total ?? 0), 10) || 0;
    if (item.children && item.children.length > 0) {
      total += item.children.reduce((sum: number, child: OrderItem) => sum + (parseInt(String(child.so_order_item_total ?? 0), 10) || 0), 0);
    }
    return total;
  }

  function getItemEstimatedDays(item: OrderItem): number {
    let days = parseInt(String(item.so_order_item_deadline_days ?? 0)) || 0;
    if (item.children && item.children.length > 0) {
      days += item.children.reduce((sum: number, child: OrderItem) => sum + (parseInt(String(child.so_order_item_deadline_days ?? 0)) || 0), 0);
    }
    return days;
  }

  function statusLabel(status?: string): string {
    return getOrderStatus(status).label;
  }

  function financialStatusLabel(status?: string): string {
    const map: Record<string, string> = { a_receber: t('A receber'), recebimento_parcial: t('Parcial'), recebido: t('Recebido') };
    return (status && map[status]) || status || '';
  }

  // ---------------------------------------------------------------------------
  // Methods (async)
  // ---------------------------------------------------------------------------
  async function loadOrder(): Promise<void> {
    const orderId = orderIdRef.value;
    if (!orderId) return;

    log('useCheckout', 'loadOrder', 'Loading order', orderId);
    loading.value = true;
    error.value = null;
    try {
      const orderIdNum = Number(orderId);
      order.value = await getOrder(orderIdNum) as unknown as OrderData;
      items.value = await fetchItems(orderIdNum) as unknown as OrderItem[];
      log('useCheckout', 'loadOrder', 'Loaded', { order: order.value, items: items.value.length });
    } catch (err) {
      logError('useCheckout', 'loadOrder', 'Error', err);
      error.value = err instanceof Error ? err.message : 'Erro ao carregar ordem';
      throw err;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Advisory received-floor guard (centavos). Mirrors the backend invariant
   * (`so_order_total` must never drop below the received sum) so an obviously
   * floor-breaching edit is blocked client-side with a clear message instead of
   * round-tripping. The authoritative guard is still the backend — this only
   * short-circuits the request when we can already tell it would be rejected.
   *
   * Returns true when the projected total is still >= received (edit may proceed);
   * returns false and surfaces a blocking globalMessage.error otherwise.
   */
  function assertTotalNotBelowReceived(projectedTotalCents: number): boolean {
    const received = Math.round(totalRecebido.value);
    if (Math.round(projectedTotalCents) < received) {
      eventBus.emit(
        'globalMessage.error',
        t('Não é possível reduzir o total da OS abaixo do valor já recebido.'),
      );
      return false;
    }
    return true;
  }

  async function removeItem(itemId: Id): Promise<void> {
    log('useCheckout', 'removeItem', 'Removing item', itemId);
    // Advisory client-side floor check: removing an item lowers the total by its
    // subtotal. If the projected total would breach the received floor, block here
    // with a clear message rather than sending a request the backend will reject.
    const target = items.value.find((i: OrderItem) => String(i.so_order_item_id) === String(itemId));
    if (target) {
      const grouped = groupedItems.value.find((g: OrderItem) => String(g.so_order_item_id) === String(itemId));
      const removedCents = grouped ? getItemSubtotal(grouped) : getItemSubtotal(target);
      const projectedTotal = (order.value?.so_order_total || 0) - removedCents;
      if (!assertTotalNotBelowReceived(projectedTotal)) {
        return;
      }
    }
    try {
      await apiRemoveItem(Number(itemId));
      await loadOrder();
    } catch (err) {
      logError('useCheckout', 'removeItem', 'Error', err);
      // Surface the backend received-floor rejection (or any other) verbatim so
      // the user sees WHY the edit was blocked; totals are left untouched.
      eventBus.emit(
        'globalMessage.error',
        err instanceof Error ? err.message : t('Erro ao remover item'),
      );
      throw err;
    }
  }

  /**
   * Update an order item, applying the received-floor guard. When the caller can
   * predict the post-edit total (e.g. a price/priority change), it passes
   * `projectedTotalCents` so the advisory client-side check can block a
   * floor-breaching edit before sending. The authoritative guard is the backend
   * invariant — its `success=false` received-floor rejection is surfaced verbatim
   * and totals are left untouched.
   */
  async function updateItem(
    itemId: Id,
    data: Parameters<typeof apiUpdateItem>[1],
    projectedTotalCents?: number,
  ): Promise<void> {
    log('useCheckout', 'updateItem', 'Updating item', { itemId, projectedTotalCents });
    if (projectedTotalCents != null && !assertTotalNotBelowReceived(projectedTotalCents)) {
      return;
    }
    try {
      await apiUpdateItem(Number(itemId), data);
      await loadOrder();
    } catch (err) {
      logError('useCheckout', 'updateItem', 'Error', err);
      eventBus.emit(
        'globalMessage.error',
        err instanceof Error ? err.message : t('Erro ao atualizar item'),
      );
      throw err;
    }
  }

  async function confirmOrder(): Promise<void> {
    if (!order.value?.so_order_id) return;
    log('useCheckout', 'confirmOrder', 'Confirming order', order.value.so_order_id);
    try {
      await apiConfirmOrder(Number(order.value.so_order_id));
      await loadOrder();
    } catch (err) {
      logError('useCheckout', 'confirmOrder', 'Error', err);
      throw err;
    }
  }

  async function cancelOrder(reason: string): Promise<void> {
    if (!order.value?.so_order_id) return;
    log('useCheckout', 'cancelOrder', 'Cancelling order', order.value.so_order_id);
    try {
      await apiCancelOrder(Number(order.value.so_order_id), reason);
      await loadOrder();
    } catch (err) {
      logError('useCheckout', 'cancelOrder', 'Error', err);
      throw err;
    }
  }

  async function uncancelOrder(): Promise<void> {
    if (!order.value?.so_order_id) return;
    log('useCheckout', 'uncancelOrder', 'Uncancelling order', order.value.so_order_id);
    try {
      await apiUncancelOrder(Number(order.value.so_order_id));
      await loadOrder();
    } catch (err) {
      logError('useCheckout', 'uncancelOrder', 'Error', err);
      throw err;
    }
  }

  async function applyDiscount(): Promise<void> {
    if (!order.value || discountValue.value <= 0) return;
    log('useCheckout', 'applyDiscount', 'Applying discount', { value: discountValue.value, isPercentage: isPercentageDiscount.value });
    isApplyingDiscount.value = true;
    try {
      const discountType = isPercentageDiscount.value ? 'percentage' : 'fixed';
      // Backend expects both percentage and fixed scaled by 100 (R$10 → 1000, 10.5% → 1050)
      const valueToSend = Math.round(discountValue.value * 100);
      await apiUpdateDiscount(order.value.so_order_id, discountType, valueToSend);
      await loadOrder();
    } catch (err) {
      logError('useCheckout', 'applyDiscount', 'Error', err);
      throw err;
    } finally {
      isApplyingDiscount.value = false;
    }
  }

  function handleDiscountTypeChange(isPercentage: boolean): void {
    if (isPercentageDiscount.value !== isPercentage) {
      discountValue.value = 0;
      isPercentageDiscount.value = isPercentage;
    }
  }

  // ---------------------------------------------------------------------------
  // Watch orderId → reload
  // ---------------------------------------------------------------------------
  const stopWatch = watch(orderIdRef, (newId) => {
    if (newId) loadOrder();
  });

  // ---------------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------------
  return {
    order,
    items,
    loading,
    error,
    isPercentageDiscount,
    discountValue,
    isApplyingDiscount,
    groupedItems,
    discountAmount,
    valorRestante,
    totalRecebido,
    isPaid,
    canAddItems,
    hasExistingWorkOrders,
    formattedClientPhone,
    assertTotalNotBelowReceived,
    loadOrder,
    removeItem,
    updateItem,
    confirmOrder,
    cancelOrder,
    uncancelOrder,
    applyDiscount,
    apiUpdateDiscount,
    handleDiscountTypeChange,
    formatAttributeDisplayValue,
    getItemSubtotal,
    getItemEstimatedDays,
    statusLabel,
    financialStatusLabel,
  };
}
